import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ArrayIcon from '../../../assets/icons/array.svg';
import DeleteIcon from '../../../assets/icons/delete.svg';
import PrimaryModal from '../Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton } from "..";
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import InlineTooltip from '../Tooltip/InlineTooltip';

const ArrayFieldType = ({ value = [], id, label, handleChange, mandatory, element, helpText }) => {
    const [showEditor, setShowEditor] = useState(false);
    const [fields, setFields] = useState([]);
    const { content } = useContentfulContext();

    const formik = useFormik({
        initialValues: {
            inputFields: fields,
        },
        validationSchema: Yup.object({
            inputFields: element
                ? Yup.array().of(
                    Yup.string()
                    .min(+element.rules.min_length || 0, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE130} ${+element.rules.min_length || 50}`)
                    .max(+element.rules.max_length || 50, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE131} ${+element.rules.max_length || 50}`)
                    .required(content.ARRAY_FIELD_TYPE_CONTENT.error.FE129))
                : Yup.array().of(
                    Yup.string()
                    .min(+element.rules.min_length || 0, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE130} ${+element.rules.min_length || 50}`)
                    .max(+element.rules.max_length || 50, `${content.ARRAY_FIELD_TYPE_CONTENT.error.FE131} ${+element.rules.max_length || 50}`))
        }),
        onSubmit: (values) => {
            handleChange({ target: { name: id, value: values.inputFields } });
            handleHideModal()
        },
    });

    useEffect(() => {
        setFields(value || []);
        formik.setFieldValue('inputFields', value || []);
    }, [value]);

    const handleAddField = () => {
        setFields([...fields, '']);
        formik.setFieldValue('inputFields', [...fields, '']);
    };

    const handleDeleteField = (index) => {
        const updatedFields = fields.filter((_, i) => i !== index);
        setFields(updatedFields);
        formik.setFieldValue('inputFields', updatedFields);
    };

    const handleFieldChange = (index, e) => {
        const updatedFields = fields.map((field, i) =>
            i === index ? e.target.value : field
        );
        setFields(updatedFields);
        formik.setFieldValue('inputFields', updatedFields);
    };

    const handleOpenModal = () => {
        setShowEditor(true);
    };

    const handleHideModal = () => {
        setShowEditor(false);
    };

    return (
        <div className='mb-4'>
            <div className="relative" dzt-field-id={id}>
                <input
                    disabled 
                    type="text" 
                    name={id} 
                    className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer" 
                    placeholder=" " 
                    value={value} 
                    title={value}
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                />
                <label
                    htmlFor={id}
                    className="absolute text-sm text-black -translate-y-4 scale-75 top-2 z-10 origin-[0]
                                bg-gray-50 px-2 left-1 flex items-center gap-1
                                peer-focus:text-primary-2"
                >
                    <span>
                        {label} {mandatory && '*'}
                    </span>
                    {helpText && (
                        <InlineTooltip
                            content={helpText}
                            imgClass="pt-0.5"
                        />
                    )}
                </label>
                <img 
                    src={ArrayIcon} 
                    alt="Icon"
                    className='absolute right-2 sm:right-6 top-1/2 transform -translate-y-1/2 h-[18px] w-[18px] cursor-pointer'
                    onClick={handleOpenModal}
                />
            </div>

            {showEditor && (
                <PrimaryModal title={label} hideModal={handleHideModal}>
                    <div className='mb-4'>
                        <div className='p-6 space-y-6'>
                            <form onSubmit={formik.handleSubmit}>
                                {formik?.values?.inputFields?.map((fieldValue, index) => (
                                    <div className='mb-5'>
                                        <div key={index} className="relative mb-2" dzt-field-id={id}>
                                            <input
                                                type="text"
                                                name={`inputFields[${index}]`}
                                                className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                                placeholder=" "
                                                value={fieldValue}
                                                onChange={(e) => handleFieldChange(index, e)}
                                                onBlur={formik.handleBlur}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                onClick={() => handleDeleteField(index)}
                                            >
                                                <img className="h-[22px] w-[22px]" src={DeleteIcon} alt="Delete" />
                                            </button>
                                        </div>
                                        {formik.touched.inputFields && formik.touched.inputFields[index] && formik.errors.inputFields && formik.errors.inputFields[index] && (
                                            <p className="mt-2 text-sm text-red-500">{formik.errors.inputFields[index]}</p>
                                        )}
                                    </div>
                                ))}
                                <div className="flex items-center justify-end space-x-2">
                                    <SecondaryButton type="button" handleClick={handleAddField}>{content.ARRAY_FIELD_TYPE_CONTENT.add_field_cta}</SecondaryButton>
                                    <PrimaryButton type="submit">
                                        {content.ARRAY_FIELD_TYPE_CONTENT.save_btn}
                                    </PrimaryButton>
                                </div>
                            </form>
                        </div>
                    </div>
                </PrimaryModal>
            )}
        </div>
    );
};

export default ArrayFieldType;
